import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SavingsCalculator } from '../components/SavingsCalculator';
import { 
  ArrowRight, CheckCircle, Clock, Shield, Star, Quote, MapPin, Phone
} from 'lucide-react';

const steps = [
  { num: 1, title: 'Tell Us Your Job', description: 'Fill in a quick form about what you need' },
  { num: 2, title: 'We Call You Back', description: 'We\'ll discuss your project and give you a price' },
  { num: 3, title: 'Book & Relax', description: 'Schedule your appointment and we handle the rest' }
];

const testimonials = [
  {
    name: 'Sarah M.',
    location: 'Stockport',
    rating: 5,
    text: 'Brilliant service! Had my kitchen cupboard doors fixed and bathroom resealed. Professional, on time, and half the price I was quoted elsewhere.',
    service: 'General Tasks'
  },
  {
    name: 'David R.',
    location: 'Altrincham',
    rating: 5,
    text: 'Used Expert-Handyman for painting our hallway and stairs. Fantastic finish, very clean workers, and saved us over £200 compared to other quotes.',
    service: 'Decorating'
  },
  {
    name: 'Linda T.',
    location: 'Wilmslow',
    rating: 5,
    text: 'Needed multiple small jobs done - new door handles, shelf fitting, TV mounting. Perfect for the half-day rate option. Will definitely use again!',
    service: 'Day Rate'
  },
  {
    name: 'James K.',
    location: 'Cheadle',
    rating: 5,
    text: 'New fence panels fitted and old shed removed. Great communication throughout and the work was completed faster than expected. Highly recommend.',
    service: 'Outdoors'
  }
];

// Featured testimonial for hero
const heroTestimonial = {
  name: 'Linda T.',
  location: 'Wilmslow',
  text: '"Half the price I was quoted elsewhere. Professional and on time!"',
  rating: 5
};

export const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="animate-in">
      <SEO 
        title="Door Fitting, Painting, Decorating & Handyman Services Near Me | Manchester & Cheshire"
        description="Local handyman in Manchester & Cheshire. Door fitting & hanging, skirting boards, architraves, wall panelling, sanding, painting, decorating, plumbing, electrics & plastering. No VAT. Save 30-60%. Free quotes."
        keywords="handyman near me, door fitting near me, door hanging near me, skirting board fitting near me, architrave fitting near me, wall panelling near me, sanding near me, painting near me, decorating near me, plumbing near me, electrics near me, plastering near me, handyman Manchester, handyman Cheshire, handyman Stockport, handyman Altrincham, carpenter near me, local handyman, affordable handyman near me"
        canonical="/"
      />

      {/* Hero Section - Simple Quote Request */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1597665863042-47e00964d899?crop=entropy&cs=srgb&fm=jpg&q=85')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/70" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium mb-4 animate-in delay-100">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              Free Quotes • No Obligation
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight font-['Outfit'] tracking-tight animate-in delay-200">
              Your Local Handyman<br />
              <span className="text-yellow-300">Manchester & Cheshire</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed animate-in delay-300">
              Door fitting, painting, decorating, skirting boards, architraves, wall panelling, plumbing, electrics & plastering. Save 30-60%. No VAT.
            </p>
            
            {/* Single Clear CTA */}
            <div className="animate-in delay-400">
              <Button 
                size="lg" 
                className="btn-accent text-lg px-10 py-7 h-auto shadow-2xl hover:scale-105 transition-transform group"
                onClick={() => navigate('/quote')}
                data-testid="hero-get-quote-btn"
              >
                Get a Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-white/70 text-sm mt-3">We&apos;ll call you back within 24 hours</p>
            </div>
            
            {/* Trust badges - directly under CTA */}
            <div className="flex flex-wrap items-center gap-3 mt-6 animate-in delay-500">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="text-white text-sm font-medium">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-white text-sm font-medium">12-Month Guarantee</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span className="text-white text-sm font-medium">No VAT</span>
              </div>
            </div>
            
            {/* Hero Testimonial */}
            <div className="mt-8 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 animate-in delay-600">
              <div className="flex items-start gap-3">
                <Quote className="w-8 h-8 text-white/40 flex-shrink-0" />
                <div>
                  <p className="text-white/90 italic text-base">{heroTestimonial.text}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <span className="text-white/70 text-sm">— {heroTestimonial.name}, {heroTestimonial.location}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trade Platform Logos Banner */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-muted-foreground mb-4">Trusted across leading trade platforms</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_repair-connect-39/artifacts/stxi6csc_Trades%20Logo.png"
            alt="Featured on Checkatrade, Rated People, TaskRabbit, MyBuilder and more"
            className="w-full max-w-4xl mx-auto"
          />
        </div>
      </section>

      {/* Savings Calculator Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-primary tracking-wide uppercase">Why Choose Us</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-['Outfit'] tracking-tight">
                Save Up To 60% On Every Job
              </h2>
              <p className="text-muted-foreground text-lg mb-6">
                No VAT. No agency fees. No hidden costs. Just honest pricing from a local tradesman you can trust.
              </p>
              <ul className="space-y-3">
                {[
                  'Direct pricing - no middleman markups',
                  'No VAT charged - the price quoted is what you pay',
                  '12-month workmanship guarantee',
                  '£15M public liability insurance'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SavingsCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-32 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-primary tracking-wide uppercase">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 font-['Outfit'] tracking-tight">
              Three Easy Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, i) => (
              <div key={step.num} className="text-center relative">
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-border" />
                )}
                <div className="relative mx-auto w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mb-6 font-['Outfit']">
                  {step.num}
                </div>
                <h3 className="text-xl font-semibold mb-2 font-['Outfit']">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary font-medium text-sm uppercase tracking-wider">What Our Customers Say</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 font-['Outfit']">
              Trusted by Homeowners Across Manchester & Cheshire
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, i) => (
              <Card key={i} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <p className="font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {testimonial.location}
                      </p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                      {testimonial.service}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Cover - Local SEO */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 font-['Outfit'] tracking-tight">
            Handyman Services Near You
          </h2>
          <p className="text-muted-foreground mb-6 text-sm md:text-base">
            Door fitting, painting, decorating, skirting boards, architraves, wall panelling, plumbing, electrics & plastering across:
          </p>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 max-w-4xl mx-auto">
            {[
              'Manchester', 'Stockport', 'Sale', 'Altrincham', 'Wilmslow', 
              'Macclesfield', 'Cheadle', 'Bramhall', 'Didsbury', 'Chorlton',
              'Salford', 'Trafford', 'Bolton', 'Bury', 'Rochdale', 'Oldham',
              'Wigan', 'Warrington', 'Knutsford', 'Hale', 'Poynton', 'Marple'
            ].map((area) => (
              <span key={area} className="px-3 py-1.5 bg-white border border-border rounded-full text-xs md:text-sm text-muted-foreground">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 hero-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 font-['Outfit'] tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Tell us about your project and we&apos;ll call you back with a free quote.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="btn-accent text-lg px-8 py-6 h-auto"
              onClick={() => navigate('/quote')}
              data-testid="cta-get-quote-btn"
            >
              Get Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:07300405045">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 h-auto border-white text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                07300 405045
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-white/70">
            {['No obligation', 'Free quotes', 'We call you back'].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
